import React, { useState, useEffect } from 'react';
import { message } from 'antd';
import { ThumbsUp, ThumbsDown, ChatCircleText } from '@phosphor-icons/react';
import Typography from '../../../components/Typography';
import Button from '../../../components/Button';
import COLORS from '../../../util/constants/colors';
import PropTypes from 'prop-types';
import './callQualityFeedback.scss';

const CallQualityFeedback = ({ callSid, isOutbound }) => {
  const [feedbackGiven, setFeedbackGiven] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Reset feedback state when voice call changes
  useEffect(() => {
    if (callSid) {
      setFeedbackGiven(false);
      setShowNotes(false);
      setSelectedFeedback(null);
      setNotes('');
      setSubmitting(false);
    }
  }, [callSid]);

  const handleFeedbackClick = (feedbackType) => {
    setSelectedFeedback(feedbackType);
    setShowNotes(true);
  };

  const submitFeedback = async () => {
    if (!selectedFeedback || !callSid) return;

    setSubmitting(true);

    try {
      const endpoint = isOutbound
        ? '/api/v1/outbound_voice_feedback'
        : '/api/v1/voice_feedback';

      const feedbackData = {
        id: callSid, // Call SID identifier
        score: selectedFeedback,
        comment: notes.trim() || undefined,
      };

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.getElementsByName('csrf-token')[0]?.content,
        },
        body: JSON.stringify(feedbackData),
      });

      if (response.status === 201) {
        // True success: feedback submitted to Langfuse
        message.success('Thank you for your feedback!');
        setFeedbackGiven(true);
        setShowNotes(false);
      } else {
        throw new Error('Failed to submit feedback');
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      message.error('Failed to submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const cancelFeedback = () => {
    setSelectedFeedback(null);
    setShowNotes(false);
    setNotes('');
  };

  if (feedbackGiven) {
    return (
      <div className="call-quality-feedback submitted">
        <div className="feedback-success">
          <ChatCircleText size={16} color={COLORS.SUCCESS_LITE} weight="fill" />
          <Typography variant="text" size="small" color={COLORS.SUCCESS_LITE}>
            Thank you for sharing your feedback!
          </Typography>
        </div>
        <div className="divider" />
      </div>
    );
  }

  return (
    <div className="call-quality-feedback">
      <Typography variant="text" size="small" color={COLORS.TEXT_SECONDARY_LITE}>
        How was the overall quality of this call?
      </Typography>

      <div className="feedback-buttons">
        <Button
          className={`feedback-btn thumbs-up ${selectedFeedback === 1 ? 'selected' : ''}`}
          type="tertiary"
          size="medium"
          icon={<ThumbsUp size={20} weight={selectedFeedback === 1 ? 'fill' : 'regular'} />}
          onClick={() => handleFeedbackClick(1)}
        />
        <Button
          className={`feedback-btn thumbs-down ${selectedFeedback === -1 ? 'selected' : ''}`}
          type="tertiary"
          size="medium"
          icon={<ThumbsDown size={20} weight={selectedFeedback === -1 ? 'fill' : 'regular'} />}
          onClick={() => handleFeedbackClick(-1)}
        />
      </div>

      {showNotes && (
        <div className="feedback-notes">
          <textarea
            className="notes-input"
            placeholder="Add notes (optional)..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
          />
          <div className="notes-actions">
            <Button
              type="tertiary"
              size="small"
              label="Cancel"
              onClick={cancelFeedback}
            />
            <Button
              type="primary"
              size="small"
              label={submitting ? 'Submitting...' : 'Submit Feedback'}
              onClick={submitFeedback}
              disabled={submitting}
            />
          </div>
        </div>
      )}
      <div className="divider" />
    </div>
  );
};

CallQualityFeedback.defaultProps = {
  callSid: null,
  isOutbound: false,
};

CallQualityFeedback.propTypes = {
  callSid: PropTypes.string,
  isOutbound: PropTypes.bool,
};

export default CallQualityFeedback;
